import React, { useState, useEffect, useRef } from "react";
import { RefreshCw, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { configService } from "../services/configService";
import toast from "react-hot-toast";
import InventorySyncScheduler from "../components/InventorySyncScheduler";
import { formatDate } from "../utils/dateUtils";
import { isTokenInvalidationError, isLoggingOut } from "../utils/errorUtils";

interface SyncStatus {
  total_changes: number;
  processed_skus: number;
  synced_adjustments: number;
  processed_skus_last_sync: number;
  synced_adjustments_last_sync: number;
  skipped_changes: number;
  failed: number;
  last_synced_at: string | null;
  is_syncing: boolean;
  sync_mode: string;
}

  interface SyncLogItem {
    id: number;
    inventory_code: string;  // Product SKU
    inventory_name: string;  // Product Name
    action: "created" | "updated" | "synced" | "initial_sync" | "delta_sync" | "sync_error";
    previous_qty?: number | null;  // Previous Qty
    new_qty?: number | null;  // New Qty
    changed_by?: string;  // Changed By
    status?: string;  // Status (success/error)
    error_message?: string | null;  // Error message if status is error
    changed_fields?: string | null; // JSON string
    reason?: string | null; // Parsed reason from backend when available
    synced_at: string | null;  // Timestamp
  }

const InventorySyncPage: React.FC = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    total_changes: 0,
    processed_skus: 0,
    synced_adjustments: 0,
    processed_skus_last_sync: 0,
    synced_adjustments_last_sync: 0,
    skipped_changes: 0,
    failed: 0,
    last_synced_at: null,
    is_syncing: false,
    sync_mode: 'live',
  });
  const [logs, setLogs] = useState<SyncLogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [query, setQuery] = useState("");
  const [showDetailsId, setShowDetailsId] = useState<number | null>(null);
  const [syncMode, setSyncMode] = useState<"live" | "test">("live");
  const [isUpdatingMode, setIsUpdatingMode] = useState(false);
  const [isModeLoading, setIsModeLoading] = useState(true);
  const [isRunningSync, setIsRunningSync] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [syncingInventory, setSyncingInventory] = useState<Set<number>>(new Set());
  const prevQueryRef = useRef(query);
  const autoRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadSyncStatus();
    loadSyncMode();
    // loadSchedule removed; SyncScheduler manages its own data
  }, []);

  useEffect(() => {
    // Only load logs after sync mode has been loaded and is not loading
    if (syncMode && !isModeLoading) {
      const didQueryChange = prevQueryRef.current !== query;
      prevQueryRef.current = query;
      if (didQueryChange && page !== 1) {
        setPage(1);
        return;
      }

      loadSyncLogs(page, query);
    }
  }, [page, query, syncMode, isModeLoading]);

  // Monitor mode changes and reload logs when mode changes
  useEffect(() => {
    if (syncMode && !isModeLoading) {
      console.log("Mode changed to:", syncMode, "- reloading logs");
      setPage(1); // Reset to first page when mode changes
      loadSyncLogs(1, query);
      loadSyncStatus();
    }
  }, [syncMode]);

  // Auto-refresh during sync operations
  useEffect(() => {
    const isSyncing = isRunningSync || syncStatus.is_syncing;
    
    if (!isSyncing) {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
      return;
    }

    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }

    autoRefreshIntervalRef.current = setInterval(async () => {
      await loadSyncStatus();
      if (syncMode && !isModeLoading) {
        await loadSyncLogs(page, query);
      }
    }, 5000);

    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, [isRunningSync, syncStatus.is_syncing, syncMode, isModeLoading, page, query]);

  const loadSyncStatus = async () => {
    try {
      const status = await configService.getInventorySyncStatus();
      setSyncStatus(status);
    } catch (error) {
      console.error("Failed to load inventory sync status:", error);
    }
  };

  // Deprecated legacy functions removed

  const loadSyncLogs = async (pageNum: number, q?: string) => {
    try {
      console.log(
        "Loading logs for mode:",
        syncMode,
        "page:",
        pageNum,
        "query:",
        q
      );
      setIsLoading(true);
      const res = await configService.getInventorySyncLogs(
        pageNum,
        perPage,
        q,
        syncMode
      );
      setLogs(res.items);
      setTotalPages(res.total_pages);
    } catch (error) {
      console.error("Failed to load inventory sync logs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSyncMode = async () => {
    try {
      setIsModeLoading(true);
      const modeConfig = await configService.getInventorySyncMode();
      console.log("Loaded sync mode:", modeConfig.mode);
      setSyncMode(modeConfig.mode);
    } catch (error) {
      console.error("Failed to load sync mode:", error);
    } finally {
      setIsModeLoading(false);
    }
  };

  const handleModeChange = async (newMode: "live" | "test") => {
    if (newMode === syncMode) return;
    try {
      setIsUpdatingMode(true);
      await configService.updateInventorySyncMode(newMode);
      setSyncMode(newMode);
      toast.success(`Inventory sync mode set to ${newMode.toUpperCase()}`);
      // Reload logs for the new mode
      setPage(1);
      await loadSyncLogs(1, query);
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message ||"Failed to update sync mode");
      }
      console.error("Failed to update sync mode:", error);
    } finally {
      setIsUpdatingMode(false);
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error("Please enter a value to search");
      return;
    }

    setPage(1);
    await loadSyncLogs(1, query);
  };

  const clearSearch = async () => {
    setQuery("");
    setPage(1);
    await loadSyncLogs(1, "");
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await loadSyncStatus();
      await loadSyncLogs(page, query);
      // Removed: await loadSchedule();
    } catch (error) {
      console.error("Failed to refresh data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRunNow = async () => {
    setIsRunningSync(true);
    try {
      const res = await configService.runInventorySync();
      toast.success(res.message || "Inventory sync completed");
      await loadSyncStatus();
      await loadSyncLogs(page, query);
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to run inventory sync");
      }
    } finally {
      setIsRunningSync(false);
    }
  };

  const handleSyncInventory = async (logId: number,inventoryCode: string) => {
    setSyncingInventory(prev => new Set(prev).add(logId));
    try {
      // Pre-check product presence across systems to avoid needless errors
      try {
        const check = await configService.checkProductSync(inventoryCode, syncMode);
        const shipheroOk = !!check?.shiphero?.success;
        if (!shipheroOk) {
          toast.error(`SKU ${inventoryCode} not found in ShipHero (${syncMode}). Sync product first.`);
          return;
        }
      } catch (precheckErr) {
        // If pre-check fails, proceed to attempt sync but log the issue
        console.warn('Product pre-check failed:', precheckErr);
      }

      const res = await configService.syncInventoryByCode(inventoryCode);
      toast.success(res.message || `Inventory ${inventoryCode} synced successfully`);
      await loadSyncStatus();
      await loadSyncLogs(page, query);
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || `Failed to sync inventory ${inventoryCode}`);
      }
    } finally {
      setSyncingInventory(prev => {
        const newSet = new Set(prev);
        newSet.delete(logId);
        return newSet;
      });
    }
  };



  const getStatusColor = (synced: number, total: number) => {
    if (synced === total) return "text-green-600";
    if (synced > 0) return "text-yellow-600";
    return "text-red-600";
  };

  const getChangedFieldsArray = (changed_fields?: string | null) => {
    if (!changed_fields)
      return [] as Array<{ key: string; old: any; new: any }>;
    try {
      const obj = JSON.parse(changed_fields) as Record<
        string,
        { old: any; new: any }
      >;
      return Object.entries(obj).map(([key, value]) => ({
        key,
        old: value.old,
        new: value.new,
      }));
    } catch {
      return [] as Array<{ key: string; old: any; new: any }>;
    }
  };

  const getChangedFieldsCount = (changed_fields?: string | null) => {
    return getChangedFieldsArray(changed_fields).length;
  };

  const renderActionBadge = (action: "created" | "updated" | "synced" | "initial_sync" | "delta_sync" | "sync_error" | "no_change") => {
    let color = "";
    let label = "";
    
    switch (action) {
      case "created":
        color = "bg-green-100 text-green-800";
        label = "Created";
        break;
      case "updated":
        color = "bg-blue-100 text-blue-800";
        label = "Updated";
        break;
      case "synced":
      case "initial_sync":
        color = "bg-purple-100 text-purple-800";
        label = "Initial Sync";
        break;
      case "delta_sync":
        color = "bg-indigo-100 text-indigo-800";
        label = "Delta Sync";
        break;
      case "sync_error":
        color = "bg-red-100 text-red-800";
        label = "Error";
        break;
      case "no_change":
        color = "bg-gray-100 text-gray-800";
        label = "No Change";
        break;
      default:
        color = "bg-gray-100 text-gray-800";
        label = action;
    }
    
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}
      >
        {label}
      </span>
    );
  };

  const currentLog = logs.find((l) => l.id === showDetailsId) || null;

  const pageButtons = () => {
    const buttons = [] as number[];
    const start = Math.max(1, Math.min(page - 2, Math.max(1, totalPages - 4)));
    const end = Math.min(totalPages, start + 4);
    for (let p = start; p <= end; p++) buttons.push(p);
    return buttons;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Sync</h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitor synchronization between ShipHero and Fulfil
          </p>
        </div>
        {/* Actions Bar */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          {/* Mode control */}
          <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-md border">
            <span className="text-sm font-medium text-gray-700">Mode</span>
            {isModeLoading ? (
              <div className="inline-flex items-center px-3 py-1.5 text-sm text-gray-500">
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </div>
            ) : (
              <div className="inline-flex rounded border overflow-hidden">
                <button
                  onClick={() => handleModeChange("live")}
                  disabled={isUpdatingMode || isRunningSync || syncStatus.is_syncing}
                  className={`${
                    syncMode === "live"
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  } px-3 py-1.5 text-sm font-medium focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Live
                </button>
                <button
                  onClick={() => handleModeChange("test")}
                  disabled={isUpdatingMode || isRunningSync || syncStatus.is_syncing}
                  className={`${
                    syncMode === "test"
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  } px-3 py-1.5 text-sm font-medium border-l focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Test
                </button>
              </div>
            )}
            {isUpdatingMode && (
              <RefreshCw className="ml-1 h-4 w-4 animate-spin text-primary-600" />
            )}
          </div>

          {/* Run Now - Consolidated with sync status */}
          <button
            onClick={handleRunNow}
            disabled={isRunningSync || syncStatus.is_syncing}
            className={`inline-flex items-center justify-center px-3 py-2 text-sm rounded-md border ${
              isRunningSync || syncStatus.is_syncing
                ? "border-yellow-600 text-yellow-700 bg-yellow-50"
                : "border-primary-600 text-primary-700 bg-white hover:bg-primary-50"
            } disabled:opacity-50`}
          >
            {isRunningSync || syncStatus.is_syncing ? (
              <span className="inline-flex items-center">
                <RefreshCw className="h-5 w-5 mr-2 animate-spin" /> Sync Running...
              </span>
            ) : (
              <span>Run Now</span>
            )}
          </button>

          {/* Refresh */}
          <button
            onClick={refreshData}
            disabled={isRefreshing}
            className="inline-flex items-center px-3 py-2 rounded-md text-sm border bg-white hover:bg-gray-50"
          >
            <RefreshCw
              className={`h-5 w-5 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Sync Scheduler section - moved to top */}
      <InventorySyncScheduler />

      {/* Filters row - moved below scheduler */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Search by product name, product code"
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={handleSearch}
            className="px-3 py-2 text-sm rounded-md border bg-white hover:bg-gray-50"
          >
            Search
          </button>
          <button
            onClick={clearSearch}
            className="px-3 py-2 text-sm rounded-md border bg-white hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Sync Status Summary */}
      <div className="text-sm text-gray-700">
        {/* <span className="mr-6">
          <span className="text-gray-500">Total Changes:</span>{" "}
          <span className="font-semibold">{syncStatus.total_changes}</span>
        </span> */}
        <span className="mr-6">
          <span className="text-gray-500">Processed SKUs (Total):</span>{" "}
          <span className="font-semibold">{syncStatus.processed_skus}</span>
        </span>
        <span className="mr-6">
          <span className="text-gray-500">Synced Adjustments (Total):</span>{" "}
          <span className="font-semibold">{syncStatus.synced_adjustments}</span>
        </span>
        <span className="mr-6">
          <span className="text-gray-500">Processed SKUs (Last Sync):</span>{" "}
          <span className="font-semibold">{syncStatus.processed_skus_last_sync}</span>
        </span>
        <span className="mr-6">
          <span className="text-gray-500">Synced Adjustments (Last Sync):</span>{" "}
          <span className="font-semibold">{syncStatus.synced_adjustments_last_sync}</span>
        </span>
        {syncStatus.failed > 0 && (
          <span className="mr-6">
            <span className="text-red-500">Failed:</span>{" "}
            <span className="font-semibold text-red-600">{syncStatus.failed}</span>
          </span>
        )}
        <span>
          <span className="text-gray-500">Last Sync:</span>{" "}
          <span className="font-semibold">
            {formatDate(syncStatus.last_synced_at)}
          </span>
        </span>
      </div>

      {/* Inventory Sync Logs */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Inventory Sync Logs
            </h3>
            {!isModeLoading && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Showing logs for:</span>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    syncMode === "live"
                      ? "bg-green-100 text-green-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {syncMode.toUpperCase()} Mode
                </span>
              </div>
            )}
          </div>
          <div className="mt-4">
            {isModeLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-center">
                  <RefreshCw className="h-8 w-8 text-primary-600 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Loading sync mode...</p>
                </div>
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No inventory sync logs
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  No inventory has been synced yet in {syncMode} mode.
                </p>
                <p className="mt-1 text-sm text-gray-400">
                  Try switching to the other mode or check if inventory exists in
                  the selected mode.
                </p>
              </div>
            ) : (
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        Product SKU
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Product Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Previous Qty
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        New Qty
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Changed By
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Status
                      </th>
                      {/* <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Reason
                      </th> */}
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Timestamp
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {log.inventory_code}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                          {log.inventory_name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                          {log.previous_qty ?? '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                          {log.new_qty ?? '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                          {log.changed_by || 'system'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                          {log.status === 'error' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Error
                            </span>
                          ) : log.status === 'pending' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Success
                            </span>
                          )}
                        </td>
                        {/* <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">
                          {getReason(log) || '-'}
                        </td> */}
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {formatDate(log.synced_at)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setShowDetailsId(log.id)}
                              className="px-2 py-1 border rounded hover:bg-gray-50"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleSyncInventory(log.id, log.inventory_code)}
                              disabled={
                                isRunningSync || 
                                syncStatus.is_syncing || 
                                syncingInventory.has(log.id) ||
                                isUpdatingMode ||
                                isModeLoading
                              }
                              className={`px-2 py-1 border rounded text-xs font-medium ${
                                isRunningSync || 
                                syncStatus.is_syncing || 
                                syncingInventory.has(log.id) ||
                                isUpdatingMode ||
                                isModeLoading
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                  : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                              }`}
                            >
                              {syncingInventory.has(log.id) ? (
                                <div className="flex items-center">
                                  <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                                  Syncing...
                                </div>
                              ) : (
                                'Sync Now'
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing page <span className="font-medium">{page}</span> of{" "}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav
                className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                aria-label="Pagination"
              >
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {pageButtons().map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                      p === page
                        ? "z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                        : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
          </div>
        </div>
      </div>


      {/* Details Modal */}
      {showDetailsId && currentLog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Inventory Sync Details
                </h3>
                <p className="text-xs text-gray-500">
                  Detailed field differences for this sync event
                </p>
              </div>
              <button
                onClick={() => setShowDetailsId(null)}
                className="px-3 py-1 border rounded hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            {/* Meta */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
              <div className="p-3 rounded bg-gray-50">
                <div>
                  <span className="font-medium">Inventory Code:</span>{" "}
                  {currentLog.inventory_code}
                </div>
                <div>
                  <span className="font-medium">Inventory Name:</span>{" "}
                  {currentLog.inventory_name}
                </div>
              </div>
              <div className="p-3 rounded bg-gray-50">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Action:</span>{" "}
                  {renderActionBadge(currentLog.action)}
                </div>
                <div>
                  <span className="font-medium">Synced At:</span>{" "}
                  {formatDate(currentLog.synced_at)}
                </div>
              </div>
            </div>

            {/* Additional context */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-xs">
              <div className="p-3 rounded bg-gray-50">
                <div className="text-gray-500">Previous Qty</div>
                <div className="text-gray-900 font-medium">
                  {currentLog.previous_qty ?? "-"}
                </div>
              </div>
              <div className="p-3 rounded bg-gray-50">
                <div className="text-gray-500">New Qty</div>
                <div className="text-gray-900 font-medium">
                  {currentLog.new_qty ?? "-"}
                </div>
              </div>
              <div className="p-3 rounded bg-gray-50">
                <div className="text-gray-500">Changed By</div>
                <div className="text-gray-900 font-medium">
                  {currentLog.changed_by || "system"}
                </div>
              </div>
            </div>

            {/* Status, Reason & Error */}
            <div className="mb-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">Status:</span>
                {currentLog.status === "error" ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Error
                  </span>
                ) : currentLog.status === 'pending' ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Pending
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Success
                  </span>
                )}
              </div>
              {getReason(currentLog) && (
                <p className="mt-2 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                  <span className="font-medium">Reason:</span> {getReason(currentLog)}
                </p>
              )}
              {currentLog.error_message && (
                <p className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                  {currentLog.error_message}
                </p>
              )}
            </div>

            {/* Changed fields table */}
            <div className="border rounded">
              <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium">
                Changed Fields ({getChangedFieldsCount(currentLog.changed_fields)})
              </div>
              <div className="max-h-96 overflow-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">
                        Field
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">
                        Old
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">
                        New
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {getChangedFieldsArray(currentLog.changed_fields).map((row, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 text-gray-900 font-medium">{row.key}</td>
                        <td className="px-4 py-2 text-gray-600 break-all">{String(row.old ?? "")}</td>
                        <td className="px-4 py-2 text-gray-900 break-all">{String(row.new ?? "")}</td>
                      </tr>
                    ))}
                    {getChangedFieldsCount(currentLog.changed_fields) === 0 && (
                      <tr>
                        <td className="px-4 py-4 text-center text-gray-500" colSpan={3}>
                          No field-level changes captured.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventorySyncPage;
  const getReason = (log: SyncLogItem) => {
    if (log.reason) return log.reason;
    if (!log.changed_fields) return null;
    try {
      const cf = JSON.parse(log.changed_fields);
      if (cf && typeof cf === 'object') {
        return cf.reason || null;
      }
      return null;
    } catch (e) {
      return null;
    }
  };
